import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { THEMES, getSavedTheme, saveTheme } from '../utils/themes';
import './Settings.css';

const Settings = ({ onBack }) => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('password');
  const [currentTheme, setCurrentTheme] = useState(getSavedTheme());

  const handlePickTheme = (name) => {
    saveTheme(name);
    setCurrentTheme(name);
  };
  
  // Password change state
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordMessage, setPasswordMessage] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [passwordLoading, setPasswordLoading] = useState(false);
  
  // Profile state
  const [displayName, setDisplayName] = useState(user?.displayName || user?.username || '');
  const [profileMessage, setProfileMessage] = useState('');
  const [profileError, setProfileError] = useState('');
  const [profileLoading, setProfileLoading] = useState(false);

  const handleChangePassword = async (e) => {
    e.preventDefault();
    setPasswordError('');
    setPasswordMessage('');
    
    // Validation
    if (!currentPassword || !newPassword || !confirmPassword) {
      setPasswordError('All fields are required');
      return;
    }
    
    if (newPassword.length < 6) {
      setPasswordError('New password must be at least 6 characters');
      return;
    }
    
    if (newPassword !== confirmPassword) {
      setPasswordError('New passwords do not match');
      return;
    }
    
    if (currentPassword === newPassword) {
      setPasswordError('New password must be different from current password');
      return;
    }
    
    setPasswordLoading(true);
    
    try {
      const token = localStorage.getItem('authToken');
      const { API_BASE_URL } = await import('../utils/apiBase');
      const response = await fetch(`${API_BASE_URL}/settings/change-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          currentPassword,
          newPassword
        })
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        setPasswordError(data.error || 'Failed to change password');
        return;
      }
      
      setPasswordMessage('Password changed successfully!');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      
      // Clear success message after 3 seconds
      setTimeout(() => setPasswordMessage(''), 3000);
      
    } catch (err) {
      console.error('Error changing password:', err);
      setPasswordError('Network error. Please try again.');
    } finally {
      setPasswordLoading(false);
    }
  };

  const handleUpdateProfile = async (e) => {
    e.preventDefault();
    setProfileError('');
    setProfileMessage('');
    
    if (!displayName.trim()) {
      setProfileError('Display name is required');
      return;
    }
    
    setProfileLoading(true);
    
    try {
      const token = localStorage.getItem('authToken');
      const { API_BASE_URL } = await import('../utils/apiBase');
      const response = await fetch(`${API_BASE_URL}/settings/profile`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          displayName: displayName.trim()
        })
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        setProfileError(data.error || 'Failed to update profile');
        return;
      }
      
      setProfileMessage('Profile updated successfully!');
      
      // Clear success message after 3 seconds
      setTimeout(() => setProfileMessage(''), 3000);
      
    } catch (err) {
      console.error('Error updating profile:', err);
      setProfileError('Network error. Please try again.');
    } finally {
      setProfileLoading(false);
    }
  };

  return (
    <div className="settings-container">
      <div className="settings-header">
        <button onClick={onBack} className="back-button">
          ← Back
        </button>
        <h1>⚙️ Settings</h1>
      </div>

      <div className="settings-content">
        <div className="settings-tabs">
          <button
            className={`settings-tab ${activeTab === 'password' ? 'active' : ''}`}
            onClick={() => setActiveTab('password')}
          >
            🔒 Change Password
          </button>
          <button
            className={`settings-tab ${activeTab === 'profile' ? 'active' : ''}`}
            onClick={() => setActiveTab('profile')}
          >
            👤 Profile
          </button>
          <button
            className={`settings-tab ${activeTab === 'account' ? 'active' : ''}`}
            onClick={() => setActiveTab('account')}
          >
            📋 Account Info
          </button>
          <button
            className={`settings-tab ${activeTab === 'theme' ? 'active' : ''}`}
            onClick={() => setActiveTab('theme')}
          >
            🎨 Board Theme
          </button>
        </div>

        <div className="settings-panel">
          {activeTab === 'password' && (
            <div className="settings-section">
              <h2>Change Password</h2>
              <form onSubmit={handleChangePassword} className="settings-form">
                <div className="form-group">
                  <label htmlFor="currentPassword">Current Password</label>
                  <input
                    type="password"
                    id="currentPassword"
                    value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                    placeholder="Enter current password"
                    disabled={passwordLoading}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="newPassword">New Password</label>
                  <input
                    type="password"
                    id="newPassword"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="Enter new password (min 6 characters)"
                    disabled={passwordLoading}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="confirmPassword">Confirm New Password</label>
                  <input
                    type="password"
                    id="confirmPassword"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="Re-enter new password"
                    disabled={passwordLoading}
                  />
                </div>

                {passwordError && <div className="error-message">{passwordError}</div>}
                {passwordMessage && <div className="success-message">{passwordMessage}</div>}

                <button type="submit" className="submit-button" disabled={passwordLoading}>
                  {passwordLoading ? 'Changing Password...' : 'Change Password'}
                </button>
              </form>
            </div>
          )}

          {activeTab === 'profile' && (
            <div className="settings-section">
              <h2>Update Profile</h2>
              <form onSubmit={handleUpdateProfile} className="settings-form">
                <div className="form-group">
                  <label htmlFor="displayName">Display Name</label>
                  <input
                    type="text"
                    id="displayName"
                    value={displayName}
                    onChange={(e) => setDisplayName(e.target.value)}
                    placeholder="Enter display name"
                    disabled={profileLoading}
                  />
                </div>

                {profileError && <div className="error-message">{profileError}</div>}
                {profileMessage && <div className="success-message">{profileMessage}</div>}

                <button type="submit" className="submit-button" disabled={profileLoading}>
                  {profileLoading ? 'Updating...' : 'Update Profile'}
                </button>
              </form>
            </div>
          )}

          {activeTab === 'theme' && (
            <div className="settings-section">
              <h2>Board Theme</h2>
              <p className="theme-help">Pick a board color palette. Applied instantly and remembered for next time.</p>
              <div className="theme-grid">
                {Object.entries(THEMES).map(([name, theme]) => (
                  <button
                    key={name}
                    type="button"
                    className={`theme-card ${currentTheme === name ? 'selected' : ''}`}
                    onClick={() => handlePickTheme(name)}
                  >
                    <div className="theme-preview" aria-hidden="true">
                      <div className="theme-preview-row">
                        <div style={{ background: theme.light }} />
                        <div style={{ background: theme.dark }} />
                        <div style={{ background: theme.light }} />
                        <div style={{ background: theme.dark }} />
                      </div>
                      <div className="theme-preview-row">
                        <div style={{ background: theme.dark }} />
                        <div style={{ background: theme.light }} />
                        <div style={{ background: theme.dark }} />
                        <div style={{ background: theme.light }} />
                      </div>
                    </div>
                    <div className="theme-label">{theme.label}</div>
                    {currentTheme === name && <div className="theme-check">✓ Active</div>}
                  </button>
                ))}
              </div>
            </div>
          )}

          {activeTab === 'account' && (
            <div className="settings-section">
              <h2>Account Information</h2>
              <div className="account-info">
                <div className="info-row">
                  <span className="info-label">Username:</span>
                  <span className="info-value">{user?.username}</span>
                </div>
                <div className="info-row">
                  <span className="info-label">Email:</span>
                  <span className="info-value">{user?.email}</span>
                </div>
                <div className="info-row">
                  <span className="info-label">Display Name:</span>
                  <span className="info-value">{user?.displayName || user?.username}</span>
                </div>
                <div className="info-row">
                  <span className="info-label">Rating:</span>
                  <span className="info-value">{user?.rating || 1200}</span>
                </div>
                <div className="info-row">
                  <span className="info-label">Games Played:</span>
                  <span className="info-value">{user?.gamesPlayed || 0}</span>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Settings;
